import { Box, Container, Tab, TabList, TabPanel, TabPanels, Tabs, useToast } from '@chakra-ui/react';
import { PaginationNew } from 'components/base/PaginationNew';
import MainSearch from 'components/nav/MainSearch';
import { List } from 'components/search/list';
import NoResults from 'components/search/noResults';
import { toTitleCase } from 'helpers/textCase';
import { PAGINATION_LIST_LIMIT } from 'lib/constants';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { withTranslations } from 'store/translations';
import api from 'utils/api';
import getQueryParameter from 'utils/getQueryParameter';
import { DocumentType, SuccessResponse } from 'utils/types/ankiro';

type Query = {
  query: string;
  page: string;
  category: string;
  locale?: string;
};

const SearchPage = () => {
  const toast = useToast({});
  const { common, category } = withTranslations();
  const {
    query: { query },
    replace,
    locale,
  } = useRouter();
  const [results, setResults] = useState<DocumentType[]>([]);
  const [groupedResults, setGroupedResults] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [tabs, setTabs] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState('allResults');
  const [isFetched, setIsFetched] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isNewQuery, setIsNewQuery] = useState(false);

  const getSearchResults = useCallback(async (qs: Query) => {
    try {
      setIsFetched(false);
      setIsFetching(true);
      const { results, total, grouped } = await api.get<SuccessResponse>('/api/getSearchResults', qs);
      setResults(results);
      qs.category === 'allResults' && setTabs(['allResults', ...Object.keys(grouped)]);
      qs.category === 'allResults' && setTotalResults(total);
      setGroupedResults((prev) => (isNewQuery ? grouped : { ...prev, ...grouped }));
      setIsFetched(true);
      setIsFetching(false);
      setIsNewQuery(false);
    } catch (e) {
      toast({
        description: 'Something went wrong while trying to fetch search results. Please try again later.',
        duration: 10000,
        isClosable: true,
        position: 'top-right',
        status: 'error',
      });
    }
  }, []);

  const triggerSearch = ({ reset = false }: { reset?: boolean }) => {
    const newQuery = getQueryParameter(query);
    if (newQuery) {
      const query: Query = {
        page: `${reset ? 1 : currentPage}`,
        query: newQuery,
        category: reset ? 'allResults' : selectedTab,
        locale,
      };
      getSearchResults(query);
    }
  };

  useEffect(() => {
    !isNewQuery && triggerSearch({});
  }, [currentPage, selectedTab]);

  useEffect(() => {
    setIsNewQuery(true);
    handlePaginationChange(1);
    setSelectedTab('allResults');
    triggerSearch({ reset: true });
  }, [query]);

  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
    replace(
      {
        query: {
          query,
          page,
        },
      },
      undefined,
      {
        shallow: true,
      },
    );
  };
  const handleTabChange = (index: number) => {
    setSelectedTab(tabs[index]);
  };
  return (
    <Container>
      <Box py={{ base: 'xs', md: 'l' }}>
        <MainSearch placeholder='Search keywords' mode='standalone' />
      </Box>
      {totalResults || !isFetched ? (
        <Container>
          <Tabs as='nav' w='100%' index={tabs.indexOf(selectedTab)} onChange={handleTabChange}>
            <TabList
              w='100%'
              position='relative'
              height='100%'
              borderBottom='0px solid'
              borderBottomColor={'black'}
              overflowX='auto'
              sx={{
                '::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              {tabs.map((tab) => {
                return (
                  <Tab
                    key={tab}
                    mb='2px'
                    _first={{ paddingLeft: '0' }}
                    _last={{ paddingRight: '0' }}
                    w='auto'
                    px='xs'
                    borderBottom='1px solid'
                    borderColor='mountain.20'
                    whiteSpace='nowrap'
                    onClick={() => {
                      setSelectedTab(tab);
                      handlePaginationChange(1);
                    }}
                  >
                    {toTitleCase(tab === 'allResults' ? common(tab) : category(tab))} (
                    {tab == 'allResults' ? totalResults : groupedResults[tab]})
                  </Tab>
                );
              })}
            </TabList>
            <TabPanels pt='s' opacity={!isFetched ? 0.2 : 1} transition='opacity .4s ease-in-out'>
              {tabs.map((tab) => (
                <TabPanel key={tab} p='0'>
                  <List
                    list={results}
                    totalItems={tab == 'allResults' ? totalResults : groupedResults[tab]}
                    page={currentPage}
                  />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
          <Box display='flex' justifyContent='center' pt='s'>
            <PaginationNew
              total={selectedTab == 'allResults' ? totalResults : groupedResults[selectedTab]}
              perPage={PAGINATION_LIST_LIMIT}
              current={currentPage}
              onNavigate={handlePaginationChange}
            />
          </Box>
        </Container>
      ) : (
        !isFetching && <NoResults keyword={query} />
      )}
    </Container>
  );
};

export default SearchPage;
